/**
 * Cursor image scrubbing on product-card media boxes (snippets/product-card.liquid):
 * moving the pointer horizontally across a <card-media-scrub> slides through
 * the product's images like a film strip - image N+1 slides in from the right as
 * the cursor moves right (and from the left moving left) - rather than
 * cross-fading in place. Every candidate image stays in the DOM at all
 * times, positioned via a per-image `--scrub-offset` custom property
 * (component-product-card.css translates each image by
 * `--scrub-offset * 100%`, so offset 0 is the visible slide and offset
 * ±1/±2/±3 sit just outside the clipped media box); never rewriting an
 * <img>'s src, which would re-fetch/re-decode on every move.
 *
 * The strip STEPS rather than tracking the cursor continuously. The media box
 * is divided into one zone per hover image, and the strip only moves when the
 * pointer crosses into a different zone - so a slide is a deliberate whole
 * step, and small cursor jitter changes nothing. Entering the card simply
 * slides the strip from wherever it rests to the zone the pointer landed in,
 * exactly like a carousel being told to select - no overlays, no placement
 * frames. One image occupies the box at any instant, which matters because
 * the images can carry mix-blend-mode (see goHome for the history).
 *
 * Retargeting mid-slide is safe because this is a CSS transition on one
 * transform, not a queue of per-step animations: a new target redirects the
 * slide in flight from wherever it has reached.
 *
 * Smoothness comes from three things:
 * 1. On the first pointerenter, every candidate image's decode() is kicked
 *    off up front (they're already in the DOM with real src/srcset by then,
 *    just possibly still decoding). Scrubbing only ever lands on an image
 *    that has finished decoding - until decode resolves, the cursor is
 *    clamped to the images that are ready (image 0 at minimum, since it's
 *    always in the initial DOM paint).
 * 2. pointermove is throttled to one update per animation frame, so the
 *    strip moves at most once per frame no matter how many events fire.
 * 3. Position changes are deduplicated: setPosition() early-returns when the
 *    zone hasn't changed, so moving within a zone does no style work at all.
 *
 * Mouse/pen only (checked via event.pointerType): touch devices never fire
 * pointerenter/pointermove with a non-touch pointerType here, so they keep
 * the plain CSS hover-swap between the primary and hover images untouched
 * (component-product-card.css only gates that CSS off once this element
 * actually engages, via [data-scrub-active] on the card - set synchronously
 * in ensureLoaded, before any paint, so the plain hover fallback never gets
 * a chance to flash in first). A no-JS visitor never runs this file at all,
 * so they get the same CSS-only fallback.
 *
 * Only cards with more than one product image render a <card-media-scrub>
 * in the first place (see product-card.liquid's has_scrub) - single-image
 * products keep the plain, non-custom-element <div> markup and never load
 * this element at all.
 */
(function () {
  if (customElements.get('card-media-scrub')) return;

  function isScrubPointer(event) {
    return event.pointerType === 'mouse' || event.pointerType === 'pen';
  }

  class CardMediaScrub extends HTMLElement {
    connectedCallback() {
      this.loaded = false;
      this.images = [];
      this.ready = [];
      this.segments = [];
      // NaN (not 0) so the first setPosition(0) in ensureLoaded still lays
      // out every image's offset instead of short-circuiting on "already
      // there" before any --scrub-offset has ever been set.
      this.position = NaN;
      this.activeSegment = -1;
      this.pendingEvent = null;
      this.rafId = null;
      this.card = null;
      // The return-home gesture owns three pieces of pending work - two
      // chained frames, a transitionend listener and a watchdog timer. All
      // three are tracked so cancelHome() can call the whole thing off; see
      // the comment there for what went wrong when they outlived the gesture.
      this.homeRaf = null;
      this.homeTimer = null;
      this.settleHandler = null;
      this.leaveRaf = null;
      this.lastExitRight = null;

      this.onPointerEnter = this.onPointerEnter.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerLeave = this.onPointerLeave.bind(this);
      this.processPointerMove = this.processPointerMove.bind(this);

      this.onIndicatorClick = this.onIndicatorClick.bind(this);
      this.addEventListener('click', this.onIndicatorClick);
      this.addEventListener('pointerenter', this.onPointerEnter);
      this.addEventListener('pointermove', this.onPointerMove);
      this.addEventListener('pointerleave', this.onPointerLeave);
    }

    disconnectedCallback() {
      this.cancelHome();
      this.removeEventListener('click', this.onIndicatorClick);
      this.removeEventListener('pointerenter', this.onPointerEnter);
      this.removeEventListener('pointermove', this.onPointerMove);
      this.removeEventListener('pointerleave', this.onPointerLeave);
      if (this.rafId) cancelAnimationFrame(this.rafId);
    }

    // Clones the lazily-loaded extra images (product.images[2..3]) in from
    // the <template> the first time a real pointer shows up, instead of on
    // page load - a grid of dozens of product cards would otherwise
    // preload every product's whole gallery for a feature most cards in
    // the grid will never actually be scrubbed.
    ensureLoaded() {
      if (this.loaded) return;
      this.loaded = true;

      var template = this.querySelector('template[data-card-scrub-template]');
      if (template) {
        /* The pointer is already on the card, so these are needed now. Left
           lazy, they sit translated outside the clipped box, and Firefox
           treats that as off-screen and holds the fetch. */
        var fragment = template.content.cloneNode(true);
        fragment.querySelectorAll('img[loading="lazy"]').forEach(function (img) {
          img.loading = 'eager';
        });
        this.appendChild(fragment);
        template.remove();
      }

      this.images = Array.from(this.querySelectorAll('.product-card__image'));

      /* A duplicate of the resting image is appended to the END of the strip
         so that leaving the card can carry on in the SAME direction and land
         on what looks like the default, instead of rewinding back through
         every image it just slid past. Once the slide finishes, the strip is
         silently placed back at the real image 0 - identical pixels, so the
         reset is invisible. Cloning is cheap: the browser reuses the decoded
         image, no second network request. */
      if (this.images.length > 1) {
        var parent = this.images[0].parentNode;

        var loopClone = this.images[0].cloneNode(true);
        loopClone.setAttribute('data-scrub-loop-clone', '');
        loopClone.setAttribute('aria-hidden', 'true');
        parent.appendChild(loopClone);
        this.images.push(loopClone);
        this.loopIndex = this.images.length - 1;

        /* There used to be a SECOND cover copy here - a floating "entry
           clone" that slid in over the strip so any image was one step from
           the cover. It is gone: .product-card__image can carry
           mix-blend-mode (the card_media_blend setting), and a blended layer
           cannot cover the strip - the two covers multiplied into one
           visibly darker one. One strip, no overlays, is the invariant now. */
      }

      this.ready = this.images.map(function (img) {
        return img.complete && img.naturalWidth > 0;
      });

      this.indicator = this.querySelector('[data-card-scrub-indicator]');
      this.indicatorItems = this.indicator
        ? Array.from(
            this.indicator.querySelectorAll('.product-card__scrub-segment, .product-card__scrub-thumbnail, .product-card__scrub-dot')
          )
        : [];

      this.card = this.closest('.product-card');
      if (this.card) {
        // Setting this here, synchronously and before the first paint under
        // the new state, is what keeps the plain CSS hover-swap fallback
        // from flashing in for a frame - see the file header comment.
        this.card.setAttribute('data-scrub-active', '');
      }

      // Lays out every image's --scrub-offset relative to position 0 before
      // anything is visible under [data-scrub-active] rules, so the very
      // first frame already shows a correct single slide instead of every
      // image stacked at offset 0 (var(--scrub-offset, 0) with nothing set
      // yet would put them all on top of each other).
      this.setPosition(0);
      this.decodeAll();
    }

    // Kicks off decode() for every candidate image up front so scrubbing
    // never has to wait on an in-flight decode mid-gesture. img.decode()
    // resolves immediately for an already-loaded image, so this is cheap
    // for image 0/1 (present since first paint) and only actually waits
    // on the freshly-cloned images 2/3.
    decodeAll() {
      this.images.forEach(
        function (img, i) {
          if (this.ready[i]) return;
          if (typeof img.decode !== 'function') {
            // No decode() support: fall back to the load event so the
            // image still becomes scrubbable once it's actually painted.
            img.addEventListener(
              'load',
              function () {
                this.ready[i] = true;
                this.applyDesired();
              }.bind(this),
              { once: true }
            );
            return;
          }
          img
            .decode()
            .then(
              function () {
                this.ready[i] = true;
                this.applyDesired();
              }.bind(this)
            )
            .catch(
              function () {
                /* Firefox rejects decode() on an image whose fetch has not
                   started yet, then loads it anyway. Treating the rejection
                   as final left every Firefox card stuck on its first hover
                   image, so fall back to the load event. */
                var mark = function () {
                  if (!img.naturalWidth) return;
                  this.ready[i] = true;
                  this.applyDesired();
                }.bind(this);
                if (img.complete) mark();
                else img.addEventListener('load', mark, { once: true });
              }.bind(this)
            );
        }.bind(this)
      );
    }

    // Highest index reachable given what has decoded so far. Images
    // decode roughly in DOM order but aren't guaranteed to, so this walks
    // from the start and stops at the first gap rather than trusting a
    // simple count.
    /* The decode clamp was a one-way door: if the cursor asked for image 1
       before it had decoded, the request was silently clamped to 0 and never
       revisited, so entering a card for the FIRST time did nothing at all
       until the pointer crossed into another zone. Re-applying the last
       intended index when a decode lands closes that. */
    applyDesired() {
      if (this.desiredIndex == null) return;
      if (!this.card || !this.card.hasAttribute('data-scrub-hovering')) return;
      this.setPosition(Math.min(this.desiredIndex, this.maxReadyIndex()));
    }

    maxReadyIndex() {
      var max = 0;
      for (var i = 0; i < this.ready.length; i++) {
        if (!this.ready[i]) break;
        max = i;
      }
      return max;
    }

    /* Calls off a return-home that is still in flight.

       goHome() leaves three things pending: two chained frames, a
       `transitionend` listener bound in CAPTURE on this element, and a 700ms
       watchdog. Nothing used to clear them when the pointer came back, and
       every one of them then fired during the NEW hover:

       - the capture listener fires on any descendant's transform transition,
         so the entry slide's own transitionend triggered the stale settle(),
         which snaps the strip to the cover with transitions off. The card
         jumped back to image 0 with the cursor still on it.
       - `goingHome` stayed true, and setPosition() forces active = -1 while it
         is, so the indicator sat idle for the whole hover.
       - the queued frames removed data-scrub-placing and stepped the strip,
         fighting the entry that had just placed it.

       Off a card and back within 700ms is not an edge case - it is what
       sweeping a mouse across a grid, or overshooting and coming back, does. */
    cancelHome() {
      if (this.leaveRaf) {
        cancelAnimationFrame(this.leaveRaf);
        this.leaveRaf = null;
      }
      clearTimeout(this.homeTimer);
      this.homeTimer = null;
      if (this.homeRaf) {
        cancelAnimationFrame(this.homeRaf);
        this.homeRaf = null;
      }
      if (this.settleHandler) {
        this.removeEventListener('transitionend', this.settleHandler, true);
        this.settleHandler = null;
      }
      this.goingHome = false;
    }

    onPointerEnter(event) {
      if (!isScrubPointer(event)) return;
      this.ensureLoaded();
      if (this.images.length <= 1) return;
      // Before anything else: a half-finished return home must not keep
      // running underneath the hover that just interrupted it.
      this.cancelHome();
      if (this.card) this.card.setAttribute('data-scrub-hovering', '');

      var rect = this.getBoundingClientRect();
      if (!rect.width) return;
      var index = this.indexForEvent(event, rect);
      if (index === null) return;
      this.desiredIndex = index;
      var target = Math.min(index, this.maxReadyIndex());

      /* The strip slides from wherever it is to the entered zone, like a
         carousel told to select - but around the RING, not down the line.
         Entering at the far right used to run 0 -> 3 as one transition, which
         swept the cover and two products through the frame on the way; frame
         recordings showed exactly that, and it read as broken. Position 0 and
         the loop clone are the same picture, so when the wrap is strictly
         shorter the strip snaps invisibly to the loop end first and animates
         a single step back - image 3 arrives alone, from the right.

         The snap is identical pixels ONLY if the strip is truly at rest.
         Re-entering while a cancelled return is still gliding means the cover
         is mid-frame, and snapping then would visibly jump it - so the wrap
         is skipped unless the cover's computed transform says settled, and
         the ordinary retargeted slide handles the rest. */
      if (
        this.position === 0 &&
        target > 0 &&
        this.loopIndex != null &&
        this.loopIndex - target < target
      ) {
        var settledAtRest = true;
        var coverStyle = getComputedStyle(this.images[0]).transform;
        if (coverStyle && coverStyle !== 'none') {
          settledAtRest = Math.abs(new DOMMatrixReadOnly(coverStyle).m41) < 1;
        }
        if (settledAtRest) {
          // Same technique as the old placement helper: force the snap to
          // paint transition-free, flush it, then let the real step animate.
          if (this.card) this.card.setAttribute('data-scrub-placing', '');
          this.setPosition(this.loopIndex);
          void this.offsetWidth;
          if (this.card) this.card.removeAttribute('data-scrub-placing');
        }
      }

      this.setPosition(target);
    }

    /* The thumbnail variant is the only interactive one. The buttons sit inside
       the card's link, so the default action has to be stopped or clicking a
       thumbnail would navigate to the product instead of changing the image. */
    onIndicatorClick(event) {
      var button = event.target.closest('[data-scrub-jump]');
      if (!button) return;
      event.preventDefault();
      event.stopPropagation();

      this.ensureLoaded();
      var index = parseInt(button.dataset.scrubJump, 10);
      if (isNaN(index)) return;
      this.desiredIndex = index;
      this.setPosition(Math.min(index, this.maxReadyIndex()));
    }

    onPointerMove(event) {
      if (!isScrubPointer(event)) return;
      if (!this.loaded || this.images.length <= 1) return;
      this.pendingEvent = event;
      if (this.rafId) return;
      this.rafId = requestAnimationFrame(this.processPointerMove);
    }

    // Resolves the cursor to a zone and steps the strip if that zone changed.
    // setPosition() early-returns on an unchanged position, so staying inside
    // one zone costs nothing.
    processPointerMove() {
      this.rafId = null;
      var event = this.pendingEvent;
      if (!event) return;
      var rect = this.getBoundingClientRect();
      if (!rect.width) return;

      var index = this.indexForEvent(event, rect);
      if (index === null) return;
      // Never scrub past an image that hasn't decoded yet - it would slide a
      // blank frame in. The request is remembered so it can be honoured the
      // moment that image is ready.
      this.desiredIndex = index;
      this.setPosition(Math.min(index, this.maxReadyIndex()));
    }

    onPointerLeave(event) {
      if (!isScrubPointer(event)) return;
      if (!this.loaded) return;
      this.pendingEvent = null;
      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
      if (this.card) this.card.removeAttribute('data-scrub-hovering');
      this.desiredIndex = null;
      /* Remembered for goHome()'s tie-break: from the exact middle both ways
         round the ring are the same length, and the hand that just left the
         card is the only sensible arbiter of which one to take. */
      var leaveRect = this.getBoundingClientRect();
      this.lastExitRight = leaveRect.width
        ? event.clientX - leaveRect.left >= leaveRect.width / 2
        : false;

      /* The return waits two frames before it starts.

         A pointer crossing the card's edge does not produce one clean leave.
         It produces leave, enter, leave, enter - alternating on consecutive
         frames - because the cursor skims the boundary rather than stepping
         cleanly over it. Measured on the real storefront while sweeping out of
         the left edge: the entry clone flipped 0 -> 1 -> 0 -> 1 more than
         forty times in a single gesture, one flip per frame, because every
         leave started a return and every enter cancelled it and re-placed the
         clone. The cover visibly stuttered, and only ever when moving fast,
         since a slow exit crosses the boundary once and keeps going.

         Deferring gives the matching enter a chance to arrive first and call
         the whole thing off via cancelHome(). Two frames is about 33ms - far
         below the threshold where a delayed return reads as lag, and enough to
         swallow the flap. If the pointer really has gone, data-scrub-hovering
         is still absent when this runs and the return proceeds as normal. */
      var element = this;
      if (this.leaveRaf) cancelAnimationFrame(this.leaveRaf);
      this.leaveRaf = requestAnimationFrame(function () {
        element.leaveRaf = requestAnimationFrame(function () {
          element.leaveRaf = null;
          if (element.card && element.card.hasAttribute('data-scrub-hovering')) return;
          element.goHome();
        });
      });
    }

    /* Which hover image the cursor is over. The box is divided into one zone
       per hover image, so the strip only moves once the pointer has crossed
       into the next zone - it does NOT track the cursor continuously. That is
       what makes it read as deliberate slides rather than the strip being
       dragged: you have to travel a real distance for anything to happen.

       Image 0 is the resting shot and has no zone; zones map onto 1..n-1. */
    indexForEvent(event, rect) {
      // The trailing loop clone is not a destination - zones map onto the real
      // hover images only, i.e. 1 .. images.length - 2.
      var last = this.images.length - 2;
      if (last < 1) return null;

      var fraction = (event.clientX - rect.left) / rect.width;
      fraction = Math.min(0.9999, Math.max(0, fraction));

      return 1 + Math.floor(fraction * last);
    }

    /* Returns to the cover around the RING, by the shorter way: the strip is
       cover, 1, 2, 3, cover-again (the loop clone), so home exists at both
       ends. From image 1 the cover is one step back, arriving from the LEFT;
       from the last image it is one step forward onto the loop clone,
       arriving from the RIGHT; from the exact middle both ways are equal and
       the side the hand exited breaks the tie.

       This is the owner's settled decision, and it took three wrong builds
       to land, so they are recorded: a floating cover overlay (broken by
       mix-blend-mode - two covers at offset 0 multiply into one darker one);
       always-from-one-side (ignores a cover sitting one step away and drags
       another copy across the frame); and a plain rewind to 0 (replays every
       image the visitor just watched - "slides all the way back" was the
       exact complaint). The ring is the model the owner described from the
       start: default, 1, 2, 3, default - exit near an end, that end's
       default slides in. One step, never a traversal, from 1 and 3 alike.

       The forward wrap lands on the loop clone, so a settle then snaps
       invisibly to the real cover - identical pixels. transitionend is
       filtered to the loop clone itself because other transforms transition
       inside the card (the indicator thumb runs the same 420ms curve); the
       1600ms watchdog covers a backgrounded tab dropping the event, and
       reduced-motion, where no transition ever ends. */
    goHome() {
      if (this.position <= 0) {
        this.setPosition(0);
        return;
      }

      var backward = this.position;
      var forward = this.loopIndex != null ? this.loopIndex - this.position : backward + 1;
      var wrapForward =
        forward < backward ||
        (forward === backward && this.lastExitRight === true);

      if (!wrapForward) {
        this.setPosition(0);
        return;
      }

      var element = this;
      this.goingHome = true;
      this.homeElement = this.images[this.loopIndex];
      this.setPosition(this.loopIndex);

      var settle = function (event) {
        if (event) {
          if (event.propertyName !== 'transform') return;
          if (event.target !== element.homeElement) return;
        }
        element.removeEventListener('transitionend', settle, true);
        element.settleHandler = null;
        clearTimeout(element.homeTimer);
        element.homeTimer = null;
        element.goingHome = false;
        /* The flush is what makes this snap invisible, and it has to be a
           forced one - NOT a frame callback.

           requestAnimationFrame runs BEFORE the browser recalculates style, so
           adding the attribute here and removing it in a frame callback added
           and removed it with no recalculation in between: the browser never
           once saw data-scrub-placing, `transition: none` never applied, and
           this "invisible" snap from the end copy back to the real cover
           animated the whole way instead - the strip gliding backwards through
           every frame, 3 then 2 then 1 then the cover, over the full 1100ms.

           That is the "it goes from three to one and then default" report, and
           it only ever showed on the way out to the RIGHT, because the
           backward branch above returns before any of this runs. Reading
           offsetWidth commits the transition-free position first, which is
           exactly what the entry wrap already does. */
        if (element.card) element.card.setAttribute('data-scrub-placing', '');
        element.setPosition(0);
        void element.offsetWidth;
        if (element.card) element.card.removeAttribute('data-scrub-placing');
      };
      this.settleHandler = settle;
      this.addEventListener('transitionend', settle, true);
      this.homeTimer = setTimeout(settle, 1600);
    }

    // Slides the film strip to a fractional `position`:    // Slides the film strip to a fractional `position`: every image's offset
    // is expressed relative to it (0 = fully visible, ±N = N slides away,
    // clipped by .product-card__media's overflow: hidden), so a position of
    // e.g. 1.4 shows image 1 sliding out to the left with image 2 coming in
    // behind it. Direction falls out of the sign of the delta - no
    // special-casing of left vs right.
    setPosition(position) {
      if (position === this.position) return;
      this.position = position;
      this.images.forEach(function (img, i) {
        img.style.setProperty('--scrub-offset', (i - position).toFixed(4));
      });

      /* Segment index is (image index - 1): image 0 is the resting shot and has
         no segment of its own. -1 therefore means NO segment is active, which
         is the honest state at rest - the card is showing the cover image, not
         any of the three hover images, so lighting the first one would claim
         otherwise. The loop clone at the end of the strip is the resting image
         too, so it also resolves to "none" rather than running off the end. */
      var index = Math.round(position);
      var active = index - 1;
      if (this.loopIndex != null && index >= this.loopIndex) active = -1;
      // Returning home: the destination is the cover image, which owns no
      // segment, so nothing should light up on the way back.
      if (this.goingHome) active = -1;

      if (active === this.activeSegment) return;
      this.activeSegment = active;
      if (!this.indicator) return;

      // One value for every indicator style: the rail variants read --active
      // in CSS, the segment/thumbnail variants use the class. Writing both
      // keeps this element ignorant of which style the merchant chose.
      // The rail variants read --active as a position multiplier, so they need a
      // real number; they express "none" by fading the whole rail instead.
      this.indicator.style.setProperty('--active', String(Math.max(0, active)));
      this.indicator.classList.toggle('is-idle', active < 0);
      if (this.indicatorItems && this.indicatorItems.length) {
        this.indicatorItems.forEach(function (item, i) {
          item.classList.toggle('is-active', i === active);
        });
      }
    }
  }

  customElements.define('card-media-scrub', CardMediaScrub);
})();
